import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { useCreateCampaign } from '../api';
import { useFormContext } from 'react-hook-form';
import IconTikTok from 'assets/icon-tiktok.svg?react';
import { CopyAll } from '@mui/icons-material';
import copy from 'copy-to-clipboard';
import { useEnqueueSnackbar } from '../../../hooks';
import useJobStatusCheck from '../../../hooks/useJobStatusCheck';

const CreateCampaignButton: FC = () => {
  const { watch } = useFormContext();
  const accountId: string = watch('accountId');
  const { onShowAlert, onShowErrorAlert } = useEnqueueSnackbar();

  const {
    data: jobStatusData,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    isError: isJobStatusError,
    clearJobStatus,
  } = useJobStatusCheck();

  const createCampaign = useCreateCampaign({
    config: {
      onSuccess: (response: Record<string, string>) => {
        if (response && typeof response === 'object' && 'job_id' in response) {
          runJubStatusCheck(response.job_id);
        } else {
          onShowErrorAlert('Job ID not received');
          console.error('Job ID not received', response);
        }
      },
      onError: () => {
        onShowErrorAlert('Failed to create campaign');
      },
    },
  });

  const handleCreateCampaign = useCallback(() => {
    createCampaign.mutate({
      ad_account_id: accountId,
    });
  }, [createCampaign, accountId]);

  const handleCloseAlert = useCallback(() => {
    createCampaign.reset();
    clearJobStatus();
  }, [createCampaign, clearJobStatus]);

  useEffect(() => {
    if (createCampaign.isError) {
      const timeoutId = setTimeout(() => {
        createCampaign.reset();
      }, 2000);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return () => null;
  }, [createCampaign]);

  const handleCopyCampaignId = useCallback(
    (value: string) => {
      copy(value);
      onShowAlert(`Campaign ID: ${value} copied!`);
    },
    [onShowAlert],
  );

  const campaignId = useMemo(() => {
    try {
      if (!jobStatusData) return null;

      if ('status' in jobStatusData && jobStatusData.status === 'failed') {
        return null;
      }

      if ('result' in jobStatusData && jobStatusData.result) {
        if (
          jobStatusData.result && typeof jobStatusData.result === 'object' && 'campaign_id' in jobStatusData.result
        ) {
          const id = jobStatusData.result.campaign_id;
          return typeof id === 'string' ? id : null;
        }
      }

      return null;
    } catch (error) {
      onShowErrorAlert('Error extracting campaign ID');
      console.error(error);
      return null;
    }
  }, [jobStatusData, onShowErrorAlert]);

  const isJobStatusErrorState = useMemo(() => {
    return isJobStatusError || jobStatusData?.status === 'failed';
  }, [isJobStatusError, jobStatusData]);

  const buttonState = useMemo(() => {
    if (createCampaign.isPending) {
      return { text: 'Creating campaign...', showIcon: true, disabled: true };
    }
    if (isJobStatusChecking && !isJobStatusFinished) {
      return { text: 'Checking status...', showIcon: true, disabled: true };
    }
    if (isJobStatusFinished && campaignId) {
      return { text: 'Campaign created!', showIcon: false, disabled: false };
    }
    if (
      createCampaign.isError ||
      isJobStatusErrorState ||
      (!campaignId && isJobStatusFinished)
    ) {
      return { text: 'API error occur...', showIcon: false, disabled: false };
    }
    return {
      text: 'Create campaign in TikTok',
      showIcon: true,
      disabled: false,
    };
  }, [
    createCampaign.isPending,
    createCampaign.isError,
    isJobStatusChecking,
    isJobStatusFinished,
    campaignId,
    isJobStatusErrorState,
  ]);

  return (
    <>
      {isJobStatusFinished && campaignId ? (
        <Box sx={{ position: 'relative' }}>
          <Alert
            icon={false}
            severity="success"
            sx={{
              position: 'absolute',
              right: 0,
              width: '480px',
              bottom: 0,
              margin: 'auto',
            }}
            onClose={handleCloseAlert}
          >
            <AlertTitle>Manual campaign created 🥷💰</AlertTitle>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
              }}
            >
              <Typography
                sx={(t) => ({
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: t.palette.success.dark,
                })}
              >
                Campaign ID: {campaignId}
              </Typography>
              <IconButton
                onClick={() => {
                  if (campaignId) {
                    handleCopyCampaignId(campaignId);
                  }
                }}
                size={'small'}
                color={'success'}
                sx={{ textTransform: 'initial', px: 1 }}
              >
                <CopyAll />
              </IconButton>
            </Box>
          </Alert>
        </Box>
      ) : (
        <Button
          variant={'outlined'}
          color={
            createCampaign.isError || isJobStatusErrorState
              ? 'error'
              : 'primary'
          }
          onClick={handleCreateCampaign}
          disabled={buttonState.disabled}
          startIcon={
            buttonState.showIcon ? (
              createCampaign.isPending || isJobStatusChecking ? (
                <CircularProgress color={'inherit'} size={18} />
              ) : (
                <IconTikTok width={20} height={24} />
              )
            ) : undefined
          }
        >
          {buttonState.text}
        </Button>
      )}
    </>
  );
};

export default CreateCampaignButton;
